import { Bot, User } from "lucide-react";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function AnimatedVoiceInteraction() {
  const [isActive, setIsActive] = useState(false);
  const [activeAgent, setActiveAgent] = useState<"bot" | "user">("bot");
  const [currentMessage, setCurrentMessage] = useState("");
  const [targetMessage, setTargetMessage] = useState("");
  const [typingSpeed, setTypingSpeed] = useState(50); // ms per character

  // Conversation sequence
  const conversation = [
    {
      agent: "bot",
      message: "Hello! I'm your VOXI assistant. How can I help you today?",
      duration: 3000,
    },
    {
      agent: "user",
      message: "I need to set up an AI agent for my customer support team.",
      duration: 2500,
    },
    {
      agent: "bot",
      message:
        "Great! I can help you create a custom AI agent for your customer support needs with natural voice capabilities.",
      duration: 4000,
    },
  ];

  // Function to simulate typing effect
  useEffect(() => {
    if (targetMessage === "") return;

    if (currentMessage === targetMessage) return;

    const timeout = setTimeout(() => {
      setCurrentMessage(targetMessage.substring(0, currentMessage.length + 1));
    }, typingSpeed);

    return () => clearTimeout(timeout);
  }, [currentMessage, targetMessage, typingSpeed]);

  useEffect(() => {
    // Simulate voice interaction flow
    let currentIndex = 0;
    let timeout: NodeJS.Timeout;
    let typingTimeout: NodeJS.Timeout;

    const runSequence = () => {
      if (currentIndex >= conversation.length) {
        // Reset after completing the sequence
        setTimeout(() => {
          currentIndex = 0;
          setIsActive(false);
          setCurrentMessage("");
          setTimeout(runSequence, 1000);
        }, 1000);
        return;
      }

      const current = conversation[currentIndex];
      setActiveAgent(current.agent as "bot" | "user");
      setIsActive(true);

      // Start typing the message
      setCurrentMessage("");
      setTargetMessage(current.message);

      // Keep active for the specified duration
      timeout = setTimeout(() => {
        setIsActive(false);
        currentIndex++;

        // Pause between interactions
        typingTimeout = setTimeout(runSequence, 800);
      }, current.duration);
    };

    // Start the sequence
    runSequence();

    return () => {
      clearTimeout(timeout);
      clearTimeout(typingTimeout);
    };
  }, []);

  return (
    <div className="bg-gradient-to-r from-primary/20 to-foreground/20 rounded-2xl p-6 relative overflow-hidden">
      <div className="flex flex-col items-center justify-center h-full space-y-6">
        <div className="flex items-center justify-center w-full">
          <div className="flex items-center space-x-4">
            <div
              className={`${activeAgent === "bot" ? "bg-primary" : "bg-muted"} p-3 rounded-full`}
            >
              <Bot
                className={`h-6 w-6 ${
                  activeAgent === "bot"
                    ? "text-primary-foreground"
                    : "text-muted-foreground"
                }`}
              />
            </div>
            <div
              className={`${activeAgent === "user" ? "bg-primary" : "bg-muted"} p-3 rounded-full`}
            >
              <User
                className={`h-6 w-6 ${
                  activeAgent === "user"
                    ? "text-primary-foreground"
                    : "text-muted-foreground"
                }`}
              />
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {isActive && (
            <motion.div
              className="flex justify-center items-center space-x-1 h-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {Array.from({ length: 5 }).map((_, i) => (
                <motion.div
                  key={i}
                  className={`w-2 rounded-full ${
                    activeAgent === "bot" ? "bg-primary" : "bg-foreground"
                  }`}
                  initial={{ height: 8 }}
                  animate={{ height: [8, 32, 8] }}
                  transition={{
                    duration: 0.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.12,
                  }}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="h-16 text-center px-4 max-w-md">
          <AnimatePresence mode="wait">
            {currentMessage && (
              <motion.p
                className={`text-sm md:text-base ${activeAgent === "bot" ? "text-primary" : "text-primary"}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {currentMessage}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
